import { useGetChildrenList } from 'common/queries-and-mutations/children';
import { useGetSpousesList } from 'common/queries-and-mutations/spouse';
import Badge from 'components/Badge';
import Button from 'components/Form/Button';
import React, { useCallback, useMemo, useState } from 'react';
import male from '../../assets/male.png';
import female from '../../assets/female.png';
import { Gender } from 'apps/user/types/user.enum.type';
import { InfoModal } from './InfoModal';

export interface SpouseChildDto {
  id: string;
  name: string;
  firstName: string;
  middleName: string;
  lastName: string;
  email: string;
  gender: string;
  relationship: string;
  address: string;
  dateOfBirth: string;
  isAssigned: boolean;
}

type Props = {
  onClose: () => void;
  onAdd: (selectUsers: SpouseChildDto[]) => void;
};

export default function SelectMultipleBeneficiaryModal({
  onClose,
  onAdd
}: Props) {
  const [checkboxes, setCheckboxes] = useState<SpouseChildDto[]>([]);

  const { data: childrens } = useGetChildrenList();
  const { data: spouses } = useGetSpousesList();

  const combinedTwoArray = useCallback(() => {
    const combinedArray: SpouseChildDto[] = [];
    childrens &&
      childrens.forEach(item => {
        combinedArray.push({
          id: `Child-${item.id!}`,
          name: `${item.childFirstName} ${item.childMiddleName} ${item.childLastName}`,
          firstName: item.childFirstName,
          middleName: item.childMiddleName,
          lastName: item.childLastName,
          email: '',
          relationship: 'Child',
          gender: item.gender,
          isAssigned: item.isAssigned,
          address: '',
          dateOfBirth: item.dateOfBirth
        });
      });

    spouses &&
      spouses.forEach(item => {
        combinedArray.push({
          id: `Spouse-${item.id!}`,
          name: `${item.spouseFirstName} ${item.spouseMiddleName} ${item.spouseLastName}`,
          firstName: item.spouseFirstName,
          middleName: item.spouseMiddleName,
          lastName: item.spouseLastName,
          email: item.emailAddress,
          relationship: 'Spouse',
          gender: item.gender,
          isAssigned: item.isAssigned,
          address: item.homeAddress,
          dateOfBirth: ''
        });
      });

    return combinedArray;
  }, [childrens, spouses]);

  const normalizeResult = useMemo(() => {
    return combinedTwoArray();
  }, [combinedTwoArray]);

  const handleCheckboxChange = (value: SpouseChildDto | 'selectAll') => {
    if (value === 'selectAll') {
      setCheckboxes(prevCheckboxes =>
        prevCheckboxes.length === normalizeResult.length
          ? []
          : normalizeResult.slice()
      );
    } else {
      setCheckboxes(prevCheckboxes =>
        prevCheckboxes.some(item => item.id === value.id)
          ? prevCheckboxes.filter(item => item.id !== value.id)
          : [...prevCheckboxes, value]
      );
    }
  };

  return (
    <InfoModal
      width="w-[55%]"
      className="h-fit rounded-md px-4 pb-9 pt-5 sm:p-9"
    >
      <div>
        <p className="mb-5 font-circular text-lg font-bold text-[#353535]">
          Spouse & Children
        </p>
        <table className="table-to-print w-full px-6 py-4 text-left text-sm">
          <thead className="border-b bg-white text-xs text-[#1F4173]">
            <tr>
              <th scope="col" className="px-6 py-4">
                <input
                  type="checkbox"
                  checked={checkboxes.length === normalizeResult.length}
                  onChange={() => handleCheckboxChange('selectAll')}
                  className="h-4 w-4 rounded border-gray-300 bg-gray-100 text-blue-600 focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:ring-offset-gray-800 dark:focus:ring-blue-600 dark:focus:ring-offset-gray-800"
                />
              </th>

              <th scope="col" className="px-6 py-4">
                Name
              </th>
              <th scope="col" className="px-6 py-4">
                Email
              </th>
              <th scope="col" className="px-6 py-4">
                Relationship
              </th>
              <th scope="col" className="px-6 py-4">
                Status
              </th>
            </tr>
          </thead>
          <tbody className="px-6 py-4">
            {!normalizeResult?.length ? (
              <tr className="text-center">
                <td colSpan={5} className="py-10 text-lg">
                  No results found
                </td>
              </tr>
            ) : (
              normalizeResult?.map(user => (
                <tr
                  key={user.id}
                  className="border-b bg-white hover:bg-gray-50"
                >
                  <td className="px-6 py-4 text-xs font-normal  text-[#353535]">
                    <div className="flex items-center gap-4">
                      <input
                        checked={checkboxes.some(
                          checkbox => checkbox.id === user.id
                        )}
                        onChange={() => handleCheckboxChange(user)}
                        type="checkbox"
                        className="h-4 w-4 rounded border-gray-300 bg-gray-100 text-blue-600 focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:ring-offset-gray-800 dark:focus:ring-blue-600 dark:focus:ring-offset-gray-800"
                      />
                      <img
                        src={user.gender === Gender.FEMALE ? female : male}
                      />
                    </div>
                  </td>

                  <td className="px-6 py-4 text-xs font-normal  text-[#353535]">
                    {user.name}
                  </td>
                  <td className="px-6 py-4 text-xs font-normal text-[#353535]">
                    {user.email || 'N/A'}
                  </td>
                  <td className="px-6 py-4 text-xs font-normal text-[#353535]">
                    {user.relationship || 'N/A'}
                  </td>
                  <td className="px-6 py-4">
                    <Badge
                      title={user.isAssigned ? 'Assigned' : 'Unassigned'}
                      type={user.isAssigned ? 'success' : 'danger'}
                      size="sm"
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        <div className="mt-10 flex items-center justify-end gap-3">
          <Button
            variant="secondary"
            size="md"
            className="mb-3 w-[10rem] rounded px-[1.5rem] py-1 text-sm font-normal"
            label="Cancel"
            type="button"
            onClick={onClose}
          ></Button>
          <Button
            variant="primary"
            size="md"
            className="mb-3 w-[10rem] rounded px-[1.5rem] py-1 text-sm font-normal"
            label="Add Beneficiary"
            onClick={() => onAdd(checkboxes)}
          ></Button>
        </div>
      </div>
    </InfoModal>
  );
}
